import { useCallback, useEffect, useRef, useState } from "react";
import { fetchState, getCurrentUser, saveState } from "./api/client";
import { ConfirmDialog } from "./components/ConfirmDialog";
import { StatusBadge } from "./components/StatusBadge";
import { EditableField } from "./components/EditableField";
import { WorkstreamCard } from "./components/WorkstreamCard";
import { WorkstreamDetail } from "./components/WorkstreamDetail";
import type { TrackerState, User, Workstream } from "./types";
import { uid } from "./utils";

const POLL_INTERVAL_MS = 5000;
const SAVE_DEBOUNCE_MS = 600;

function visibleWorkstreams(state: TrackerState): Workstream[] {
  if (!state.access || state.access.mode === "full") return state.workstreams;
  const allowed = new Set(state.access.allowedWorkstreamIds);
  return state.workstreams.filter((ws) => allowed.has(ws.id));
}

function canViewOverview(state: TrackerState): boolean {
  return !state.access || state.access.mode === "full";
}

export function App() {
  const [state, setState] = useState<TrackerState | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [tab, setTab] = useState("overview");
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [loadError, setLoadError] = useState<string | null>(null);

  const saveTimer = useRef<number | null>(null);
  const dirtyRef = useRef(false);
  const stateRef = useRef<TrackerState | null>(null);

  // Initial load + identity
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const u = await getCurrentUser();
        if (cancelled) return;
        if (!u) {
          // Not signed in — redirect the browser to AAD login.
          // (Letting the API redirect happen during a fetch() fails CORS at login.microsoftonline.com.)
          const returnTo = window.location.pathname + window.location.search;
          window.location.href = `/.auth/login/aad?post_login_redirect_uri=${encodeURIComponent(returnTo)}`;
          return;
        }
        setUser(u);
        const s = await fetchState();
        if (cancelled) return;
        setState(s);
        stateRef.current = s;
      } catch (err) {
        if (!cancelled) setLoadError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Polling — pull other users' edits when local has no pending writes
  useEffect(() => {
    const id = window.setInterval(async () => {
      if (dirtyRef.current) return;
      try {
        const next = await fetchState();
        const local = stateRef.current;
        if (local && JSON.stringify(local) !== JSON.stringify(next)) {
          stateRef.current = next;
          setState(next);
        }
      } catch {
        /* swallow — next tick will retry */
      }
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(id);
  }, []);

  useEffect(() => {
    if (!state) return;

    const canUseOverview = canViewOverview(state);
    const workstreams = visibleWorkstreams(state);
    const visibleIds = new Set(workstreams.map((ws) => ws.id));
    const fallbackTab = canUseOverview ? "overview" : workstreams[0]?.id ?? "overview";

    if ((tab === "overview" && !canUseOverview) || (tab !== "overview" && !visibleIds.has(tab))) {
      if (tab !== fallbackTab) setTab(fallbackTab);
    }
  }, [state, tab]);

  const update = useCallback((next: TrackerState) => {
    setState(next);
    stateRef.current = next;
    dirtyRef.current = true;
    setSaveStatus("saving");

    if (saveTimer.current) window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(async () => {
      try {
        await saveState(next);
        dirtyRef.current = false;
        setSaveStatus("saved");
        window.setTimeout(() => setSaveStatus("idle"), 1500);
      } catch {
        setSaveStatus("error");
      }
    }, SAVE_DEBOUNCE_MS);
  }, []);

  const updateWorkstream = useCallback(
    (ws: Workstream) => {
      if (!state) return;
      update({
        ...state,
        workstreams: state.workstreams.map((w) => (w.id === ws.id ? ws : w)),
      });
    },
    [state, update],
  );

  const addWorkstream = useCallback(() => {
    if (!state) return;
    const id = `ws-${uid()}`;
    const newWs: Workstream = {
      id,
      name: "New Workstream",
      deerfieldLead: "",
      hssLead: "",
      gatingFactor: "",
      status: "Not Started",
      notes: "",
      updateLog: [],
      kind: "editable",
      columns: [
        { id: "workflow", label: "Workflow", type: "longText" },
        { id: "owner", label: "Owner", type: "text" },
        { id: "status", label: "Status", type: "status" },
        { id: "notes", label: "Notes", type: "longText" },
      ],
      rows: [],
    };
    update({ ...state, workstreams: [...state.workstreams, newWs] });
    setTab(id);
  }, [state, update]);

  // Tab order is a shared property of the document, so it moves within the full
  // workstream list rather than the subset a section user can see.
  const moveWorkstream = useCallback(
    (id: string, dir: -1 | 1) => {
      if (!state) return;
      const next = [...state.workstreams];
      const idx = next.findIndex((w) => w.id === id);
      const target = idx + dir;
      if (idx < 0 || target < 0 || target >= next.length) return;
      [next[idx], next[target]] = [next[target], next[idx]];
      update({ ...state, workstreams: next });
    },
    [state, update],
  );

  const [pendingDeleteWsId, setPendingDeleteWsId] = useState<string | null>(null);

  const deleteWorkstream = useCallback(
    (id: string) => {
      if (!state) return;
      update({ ...state, workstreams: state.workstreams.filter((w) => w.id !== id) });
      if (tab === id) setTab("overview");
    },
    [state, update, tab],
  );

  if (loadError) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-sm text-red-600">Failed to load: {loadError}</div>
      </div>
    );
  }

  if (!state) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-sm text-slate-400">Loading...</div>
      </div>
    );
  }

  const workstreams = visibleWorkstreams(state);
  const showOverview = canViewOverview(state);
  const canEditGlobals = state.access?.canEditGlobals ?? true;
  const activeWs = workstreams.find((w) => w.id === tab);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="font-bold text-slate-800">Deerfield</span>
            <span className="text-slate-300 text-lg">x</span>
            <span className="font-bold text-slate-800">HSS</span>
            <span className="text-slate-200">|</span>
            <span className="text-sm font-bold text-slate-500">Progress Status Tracker</span>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1.5 text-xs text-slate-500">
              Week of
              {canEditGlobals ? (
                <EditableField
                  value={state.weekOf}
                  onChange={(v) => update({ ...state, weekOf: v })}
                  className="text-xs text-slate-700 font-medium w-36"
                />
              ) : (
                <span className="text-xs text-slate-700 font-medium">{state.weekOf}</span>
              )}
            </div>
            {saveStatus === "saving" && (
              <span className="text-xs text-slate-400">Saving...</span>
            )}
            {saveStatus === "saved" && (
              <span className="text-xs text-emerald-500">Saved</span>
            )}
            {saveStatus === "error" && (
              <span className="text-xs text-red-500">Save failed</span>
            )}
            {user && (
              <span className="text-xs text-slate-400">
                {user.userDetails}
              </span>
            )}
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 flex overflow-x-auto items-center">
          {showOverview && (
            <button
              onClick={() => setTab("overview")}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap shrink-0 ${
                tab === "overview"
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300"
              }`}
            >
              Overview
            </button>
          )}
          {workstreams.map((ws, i) => (
            <div
              key={ws.id}
              className={`group relative flex items-center border-b-2 transition-colors shrink-0 ${
                tab === ws.id
                  ? "border-blue-500"
                  : "border-transparent hover:border-slate-300"
              }`}
            >
              {canEditGlobals && (
                <button
                  onClick={() => moveWorkstream(ws.id, -1)}
                  disabled={i === 0}
                  className="opacity-0 group-hover:opacity-100 pl-2 py-2.5 text-[10px] text-slate-300 hover:text-slate-600 disabled:opacity-0 transition-opacity"
                  title={`Move "${ws.name}" left`}
                >
                  &#9664;
                </button>
              )}
              <button
                onClick={() => setTab(ws.id)}
                className={`pl-2 pr-1 py-2.5 text-sm font-medium whitespace-nowrap ${
                  tab === ws.id ? "text-blue-600" : "text-slate-500 hover:text-slate-700"
                }`}
              >
                {ws.name}
              </button>
              {canEditGlobals && (
                <>
                  <button
                    onClick={() => moveWorkstream(ws.id, 1)}
                    disabled={i === workstreams.length - 1}
                    className="opacity-0 group-hover:opacity-100 py-2.5 text-[10px] text-slate-300 hover:text-slate-600 disabled:opacity-0 transition-opacity"
                    title={`Move "${ws.name}" right`}
                  >
                    &#9654;
                  </button>
                  <button
                    onClick={() => setPendingDeleteWsId(ws.id)}
                    className="opacity-0 group-hover:opacity-100 px-2 py-2.5 text-slate-300 hover:text-red-500 transition-opacity text-xs"
                    title={`Delete "${ws.name}" workstream`}
                  >
                    &times;
                  </button>
                </>
              )}
            </div>
          ))}
          {canEditGlobals && (
            <button
              onClick={addWorkstream}
              className="px-4 py-2.5 text-sm font-medium text-slate-400 hover:text-blue-600 whitespace-nowrap shrink-0 transition-colors"
              title="Add a new workstream"
            >
              + New workstream
            </button>
          )}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8 space-y-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {workstreams.map((ws) => (
            <WorkstreamCard
              key={ws.id}
              ws={ws}
              active={tab === ws.id}
              onClick={() => setTab(ws.id)}
              onUpdate={updateWorkstream}
            />
          ))}
        </div>

        {canEditGlobals && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white rounded-2xl ring-1 ring-amber-200 p-5 shadow-sm">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-amber-500">!</span>
                <span className="text-sm font-semibold text-slate-800">
                  Key Changes This Week
                </span>
              </div>
              <EditableField
                value={state.keyChanges}
                onChange={(v) => update({ ...state, keyChanges: v })}
                multi
                placeholder="Highlight material changes, escalations, or decisions needed..."
                className="text-sm text-slate-500 w-full"
              />
            </div>
            <div className="bg-white rounded-2xl ring-1 ring-red-200 p-5 shadow-sm">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-red-500">!</span>
                <span className="text-sm font-semibold text-slate-800">
                  Decisions / Escalations Required
                </span>
              </div>
              <EditableField
                value={state.escalations}
                onChange={(v) => update({ ...state, escalations: v })}
                multi
                placeholder="Flag items requiring CEO attention or a decision..."
                className="text-sm text-slate-500 w-full"
              />
            </div>
          </div>
        )}

        {workstreams.length === 0 ? (
          <div className="rounded-2xl ring-1 ring-slate-200 bg-white p-6 text-sm text-slate-500 shadow-sm">
            No dashboard sections are assigned to your account. Please contact Jeremy Job.
          </div>
        ) : tab === "overview" && showOverview ? (
          <div className="space-y-3">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest">
              All Workstreams
            </p>
            <div className="overflow-x-auto rounded-2xl ring-1 ring-slate-200 bg-white shadow-sm">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wide font-semibold border-b border-slate-200">
                  <tr>
                    <th className="px-5 py-3 text-left">Workstream</th>
                    <th className="px-5 py-3 text-left">DF Lead(s)</th>
                    <th className="px-5 py-3 text-left">HSS Lead(s)</th>
                    <th className="px-5 py-3 text-left">Gating Factor</th>
                    <th className="px-5 py-3 text-left">Status</th>
                    <th className="px-5 py-3 text-left">Notes / Updates</th>
                    <th className="w-8" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {workstreams.map((ws) => (
                    <tr
                      key={ws.id}
                      className="hover:bg-slate-50 cursor-pointer transition-colors"
                      onClick={() => setTab(ws.id)}
                    >
                      <td className="px-5 py-4 font-semibold text-slate-800">{ws.name}</td>
                      <td className="px-5 py-4 text-slate-600">{ws.deerfieldLead}</td>
                      <td className="px-5 py-4 text-slate-600">{ws.hssLead}</td>
                      <td className="px-5 py-4 text-slate-400 text-xs">{ws.gatingFactor}</td>
                      <td className="px-5 py-4">
                        <StatusBadge status={ws.status} />
                      </td>
                      <td className="px-5 py-4 text-slate-400 text-xs max-w-xs">
                        {ws.notes}
                      </td>
                      <td className="px-5 py-4 text-slate-300">&rsaquo;</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : activeWs ? (
          <div className="space-y-3">
            <p className="text-base font-semibold text-slate-800">{activeWs.name}</p>
            <WorkstreamDetail ws={activeWs} user={user} onUpdate={updateWorkstream} />
          </div>
        ) : null}
      </main>

      <footer className="max-w-7xl mx-auto px-6 py-8 border-t border-slate-200 mt-4">
        <p className="text-xs text-slate-300 text-center">
          Deerfield Management x Hospital for Special Surgery &middot; Confidential &middot;
          Saved to Deerfield Azure
        </p>
      </footer>

      <ConfirmDialog
        open={!!pendingDeleteWsId}
        title="Delete workstream?"
        message={(() => {
          const ws = state.workstreams.find((w) => w.id === pendingDeleteWsId);
          if (!ws) return "";
          const n = (ws.rows ?? []).length;
          return `Permanently delete "${ws.name}"? ${n} row${n === 1 ? "" : "s"} of data will be lost for every viewer. This cannot be undone from the UI.`;
        })()}
        confirmLabel="Delete workstream"
        destructive
        onConfirm={() => {
          if (pendingDeleteWsId) deleteWorkstream(pendingDeleteWsId);
          setPendingDeleteWsId(null);
        }}
        onCancel={() => setPendingDeleteWsId(null)}
      />
    </div>
  );
}
